/* eslint-disable @typescript-eslint/no-explicit-any */
import { useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import Button from 'components/base/Button';
import NewReturningTableSearch from './NewReturningTableSearch';
import NewReturningTable from './NewReturningTable';
import usePageSearchParams from 'utils/usePageSearchParams';
import returnService from 'service/return';
import returnOutService from 'service/returnOut';
import returnItemService from 'service/returnItemService';
import { showAlert } from 'store/alert/alert.thunk';
import useFocusRefs from 'utils/useFocusRefs';
import legalEntitySettingsService from 'service/legalEntitySettings';
import './styles.scss';
import { useAppContext } from 'providers/AppProvider';
import returnReasonService from 'service/returnReason';

const NewReturning = () => {
  const { t, i18n } = useTranslation();
  const {
    config: { theme }
  } = useAppContext();

  const navigate = useNavigate();
  const { pathname, state } = useLocation();
  const [searchParams] = usePageSearchParams();
  const newReturningId = pathname.split('/').pop();
  const { newreturning_pid } = state;
  const senderId = searchParams?.get('sender_id');
  const dispatch: Dispatch<any> = useDispatch();
  const { setRef, focusRef } = useFocusRefs();

  const [returnItemData, setReturnItemData] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [isAutoTab, setIsAutoTab] = useState(false);
  const [savedBarcodes, setSavedBarcodes] = useState<any>([]);
  const [selectedReasonId, setSelectedReasonId] = useState('');
  const [returnReasonOption, setReturnReasonOption] = useState<any>([]);

  //GET return Reason  to options
  useQuery(['GET_RETURN_REASON_ALL'], async () => {
    await returnReasonService
      .getList({ offset: 0, limit: 100, status: true })
      .then((res: any) => {
        const options = res?.product_return_reasons?.map((option: any) => ({
          value: option.id,
          pid: option?.pid,
          label: option.name?.[i18n?.language]
        }));

        setReturnReasonOption(options);
      });
  });

  useQuery(['GET_SETTINGS'], async () => {
    await legalEntitySettingsService
      .getList({
        offset: 0,
        limit: 20
      })
      .then((res: any) => {
        setIsAutoTab(res?.legal_entity_settings[0].is_auto_tab ?? 0);
      });
  });

  const newReturningBreadcrumbItems: PageBreadcrumbItem[] = [
    {
      label: t('returns'),
      url: '/returning/outgoing'
    },
    {
      label: t('outgoing'),
      url: '/returning/outgoing'
    },
    {
      label: t('new'),
      active: true
    }
  ];

  const sumOfOrderPrice = returnItemData?.reduce((acc: any, item: any) => {
    const priceIn = +item?.price || 0;
    const quantity = String(item?.amount ?? '0');

    let integerPart = 0;
    let numerator = 0;
    let denominator = 0; // Default denominator to 1 if no fraction

    // Check if quantity contains '/'
    if (quantity?.includes('/')) {
      [numerator, denominator] = quantity.split('/').map(Number);
      integerPart = numerator; // Whole number part
      // eslint-disable-next-line
      denominator = denominator; // Remainder of the division
    } else {
      integerPart = +quantity;
    }

    // Package quantity
    const packageQuantity = +item?.package_quantity || 1;

    const adjustedQuantity =
      item?.measure_unit_kind_pid === 1
        ? integerPart * packageQuantity + denominator
        : integerPart;

    const allCost =
      item?.measure_unit_kind_pid === 1
        ? (priceIn / packageQuantity) * +adjustedQuantity
        : priceIn * item?.amount;
    return acc + allCost;
  }, 0);

  const handleSave = () => {
    setSaveLoading(true);

    const invalidItems = returnItemData?.filter(
      (el: any) => !el?.return_reason_id
    );

    if (invalidItems.length > 0 && !selectedReasonId) {
      dispatch(
        showAlert({
          title: t('please_indicate_reason_returning')
        })
      );
      setSendLoading(false);
      return;
    }

    const selectedReason = returnReasonOption.find(
      (option: any) => option.value === selectedReasonId
    );

    const createData = returnItemData?.map((el: any) => {
      const quantity = el?.amount || '0';

      let integerPart = 0;
      let numerator = 0;
      let denominator = 0;

      const quantityStr = String(quantity);

      if (quantityStr?.includes('/')) {
        [numerator, denominator] = quantityStr.split('/').map(Number);

        integerPart = numerator; // Whole number part
        // eslint-disable-next-line
        denominator = denominator; // Remainder of the division
      } else {
        integerPart = +quantityStr;
      }

      const packageQuantity = +el?.package_quantity || 1;

      // Calculate adjusted quantity
      const adjustedQuantity =
        el?.measure_unit_kind_pid === 1
          ? integerPart * packageQuantity + denominator
          : integerPart;

      return {
        barcode: el?.barcode,
        name: el?.product_name,
        currency_id: el?.currency_id,
        currency_pid: el?.currency_pid || 0,
        measure_unit_id: el?.measure_unit_id,
        measure_unit_pid: el?.measure_unit_pid || 0,
        nomenclature_id: el?.nomenclature_id,
        nomenclature_pid: el?.nomenclature_pid || 0,
        product_id: el?.product_id,
        product_pid: el?.product_pid || 0,
        parent_product_id: el?.parent_product_id,
        parent_product_pid: el?.parent_product_pid || 0,
        return_reason_id:
          selectedReasonId !== '' ? selectedReasonId : el?.return_reason_id,
        return_reason_pid:
          selectedReasonId !== ''
            ? selectedReason?.pid
            : el?.return_reason_pid || 0,
        price: el?.price ? +el?.price : 0,
        quantity:
          el?.measure_unit_kind_pid === 1 ? +adjustedQuantity : +quantity,
        package_quantity: el?.package_quantity ? +el?.package_quantity : 0,
        status_id: 'd9f0bc1f-5245-428a-800b-d51aadde2557', // static new status id
        status_pid: 13,
        invoice_pid: el?.invoice_pid,
        order_pid: el?.order_pid || 0,
        order_item_pid: el?.order_item_pid || 0,
        invoice_payment_order_pid: el?.invoice_payment_order_pid || 0
      };
    });

    const updateData = {
      return_id: newReturningId,
      return_pid: newreturning_pid || 0,
      return_items: createData
    };

    if (newreturning_pid) {
      returnItemService.createReturnItem(updateData).then((createres: any) => {
        if (createres) {
          returnService.getReturnById(newreturning_pid).then((res: any) => {
            if (res) {
              returnOutService
                .updateReturnOut(newreturning_pid, {
                  ...res,
                  status_id: 'd9f0bc1f-5245-428a-800b-d51aadde2557',
                  status_pid: 13
                })
                .then((outresponse: any) => {
                  if (outresponse) {
                    dispatch(
                      showAlert({
                        title: t('new_application_saved'),
                        type: 'success'
                      })
                    );
                    setSaveLoading(false);
                  }
                });
            }
          });
        }
      });
    }
  };

  const handleSend = () => {
    const invalidItems = returnItemData?.filter(
      (el: any) => !el?.return_reason_id
    );

    if (invalidItems.length > 0 && !selectedReasonId) {
      dispatch(
        showAlert({
          title: t('please_indicate_reason_returning')
        })
      );
      setSendLoading(false);
      return;
    }

    const selectedReason = returnReasonOption.find(
      (option: any) => option.value === selectedReasonId
    );

    const createData = returnItemData?.map((el: any) => {
      const quantity = el?.amount || '0';

      let integerPart = 0;
      let numerator = 0;
      let denominator = 0;

      const quantityStr = String(quantity);

      if (quantityStr?.includes('/')) {
        [numerator, denominator] = quantityStr.split('/').map(Number);

        integerPart = numerator; // Whole number part
        // eslint-disable-next-line
        denominator = denominator; // Remainder of the division
      } else {
        integerPart = +quantityStr;
      }

      const packageQuantity = +el?.package_quantity || 1;

      // Calculate adjusted quantity
      const adjustedQuantity =
        el?.measure_unit_kind_pid === 1
          ? integerPart * packageQuantity + denominator
          : integerPart;

      return {
        barcode: el?.barcode,
        name: el?.product_name,
        currency_id: el?.currency_id,
        currency_pid: el?.currency_pid || 0,
        measure_unit_id: el?.measure_unit_id,
        measure_unit_pid: el?.measure_unit_pid || 0,
        nomenclature_id: el?.nomenclature_id,
        nomenclature_pid: el?.nomenclature_pid || 0,
        product_id: el?.product_id,
        product_pid: el?.product_pid || 0,
        parent_product_id: el?.parent_product_id,
        parent_product_pid: el?.parent_product_pid || 0,
        price: el?.price ? +el?.price : 0,
        quantity:
          el?.measure_unit_kind_pid === 1 ? +adjustedQuantity : +quantity,
        return_reason_id:
          selectedReasonId !== '' ? selectedReasonId : el?.return_reason_id,
        return_reason_pid:
          selectedReasonId !== ''
            ? selectedReason?.pid
            : el?.return_reason_pid || 0,
        package_quantity: el?.package_quantity ? +el?.package_quantity : 0,
        status_id: '2b22cd85-070e-4779-afcb-8f0be603b60b', // static new status id
        status_pid: 1,
        invoice_pid: el?.invoice_pid,
        order_pid: el?.order_pid || 0,
        order_item_pid: el?.order_item_pid || 0,
        invoice_payment_order_pid: el?.invoice_payment_order_pid || 0
      };
    });

    const updateData = {
      return_id: newReturningId,
      return_pid: newreturning_pid || 0,
      return_items: createData
    };

    if (newreturning_pid && createData?.length > 0) {
      setSendLoading(true);

      returnItemService.createReturnItem(updateData).then((createres: any) => {
        if (createres) {
          returnService.getReturnById(newreturning_pid).then((res: any) => {
            if (res) {
              returnOutService
                .updateReturnOut(newreturning_pid, {
                  ...res,
                  status_id: '2b22cd85-070e-4779-afcb-8f0be603b60b',
                  status_pid: 1
                })
                .then((outresponse: any) => {
                  if (outresponse) {
                    setSendLoading(false);
                    dispatch(
                      showAlert({
                        title: t('new_return_sent'),
                        type: 'success'
                      })
                    );
                    navigate('/returning/outgoing');
                  }
                });
            }
          });
        }
      });
    } else {
      dispatch(
        showAlert({
          title: t('unable_send_return')
        })
      );
    }
  };

  return (
    <div>
      <PageBreadcrumb items={newReturningBreadcrumbItems} />
      <h2>{t('new')}</h2>

      <NewReturningTableSearch
        returnItemData={returnItemData}
        setReturnItemData={setReturnItemData}
        senderId={senderId}
        focusRef={focusRef}
        isAutoTab={isAutoTab}
        setSavedBarcodes={setSavedBarcodes}
        savedBarcodes={savedBarcodes}
        setSelectedReasonId={setSelectedReasonId}
        selectedReasonId={selectedReasonId}
        returnReasonOption={returnReasonOption}
      />
      <NewReturningTable
        returnItemData={returnItemData}
        newReturningId={newReturningId}
        setReturnItemData={setReturnItemData}
        setRef={setRef}
        focusRef={focusRef}
        setSavedBarcodes={setSavedBarcodes}
        selectedReasonId={selectedReasonId}
      />

      <div
        className={
          theme === 'dark' ? 'dark-footer-return-info' : 'footer-return-info'
        }
      >
        <div className="box">
          <p className="mb-0 text-bold">{t('total_amount')}: </p>
          <span>
            {sumOfOrderPrice
              ? new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 4,
                  useGrouping: true
                })
                  .format(sumOfOrderPrice)
                  .replace(/,/g, ' ')
              : 0}
          </span>
        </div>

        <div className="main-btn-group">
          <Button
            type="button"
            variant="outline-primary"
            onClick={() => handleSave()}
            loading={saveLoading}
          >
            {t('save')}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={() => handleSend()}
            loading={sendLoading}
          >
            {t('send')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NewReturning;
